import React from 'react';
import { i18n } from 'config/locales';

import { Dispatch } from 'redux';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import Country from 'components/Form/Country';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import Location from 'components/Form/Location';
import PVQName from 'components/Form/PVQName';
import * as formTypes from 'constants/formTypes';
import { isPVQForm } from 'helpers/form';
import Dropdown from 'components/Form/Dropdown';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { foreignContactRelationshipOptions, foreignInterestCoOwnerOptions } from 'constants/enums/foreignActivityOptions';
import {
  CountryArrayModel, CountryModel, LocationModel
} from 'forms/shared/types';
import { isSafeInternational } from 'helpers/locations';
import NotApplicable from 'components/Form/NotApplicable';
import {
  CommonAddressBooks,
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
} from '../../../../../types/props';
import ValidationElement, {
  ValidationElementProps,
} from '../../../../Form/ValidationElement/ValidationElement';
import { ErrorCode } from '../../../../../redux/modules/application/reducers/error';
import { ForeignActivitiesCoOwnerItemModel } from '../../../../../forms/sections/foreign/activities/shared/foreignActivitiesCoOwner';

interface Props extends Partial<ForeignActivitiesCoOwnerItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  formType?: formTypes.FormTypes

  dispatch: Dispatch
  prefix: string
  attribute: string
}

export default class CoOwner extends ValidationElement<Props> {
  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignActivitiesCoOwnerItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Address: this.props.Address,
      Countries: this.props.Countries,
      RelationshipNature: this.props.RelationshipNature,
      // PVQ
      CoOwnerType: this.props.CoOwnerType,
      CoOwnerTypeOtherExplanation: this.props.CoOwnerTypeOtherExplanation,
      RelationshipNotApplicable: this.props.RelationshipNotApplicable,
      CountryNotApplicable: this.props.CountryNotApplicable,
      AddressNotApplicable: this.props.AddressNotApplicable,
      Relationship: this.props.Relationship,
      InUsNotApplicable: this.props.InUsNotApplicable,
      ...queue,
    });
  };

  updateName = (values: ForeignActivitiesCoOwnerItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateAddress = (values: ForeignActivitiesCoOwnerItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateCountries = (values: ForeignActivitiesCoOwnerItemModel['Countries']) => {
    this.update({
      Countries: values,
    });
  };

  updateRelationshipNature = (values: ForeignActivitiesCoOwnerItemModel['RelationshipNature']) => {
    this.update({
      RelationshipNature: values,
    });
  };

  updateCoOwnerType = (values: ForeignActivitiesCoOwnerItemModel['CoOwnerType']) => {
    this.update({
      CoOwnerType: values,
    });
  };

  updateCoOwnerOtherTypeExplanation = (values: ForeignActivitiesCoOwnerItemModel['CoOwnerTypeOtherExplanation']) => {
    this.update({
      CoOwnerTypeOtherExplanation: values,
    });
  };

  updateRelationshipNotApplicable = (values: ForeignActivitiesCoOwnerItemModel['RelationshipNotApplicable']) => {
    this.update({
      Relationship: values.applicable ? this.props.Relationship : {},
      RelationshipNotApplicable: values,
    });
  };

  updateRelationship = (values: ForeignActivitiesCoOwnerItemModel['Relationship']) => {
    this.update({
      Relationship: values,
    });
  };

  updateCountryNotApplicable = (
    values: ForeignActivitiesCoOwnerItemModel['CountryNotApplicable']
  ) => {
    this.update({
      Countries: values.applicable ? this.props.Countries : {},
      CountryNotApplicable: values,
    });
  };

  updateAddressNotApplicable = (
    addressNotApplicable: ForeignActivitiesCoOwnerItemModel['AddressNotApplicable']
  ) => {
    const queue: Partial<ForeignActivitiesCoOwnerItemModel> = { AddressNotApplicable: addressNotApplicable };

    if (addressNotApplicable?.applicable === false) {
      const address: LocationModel | undefined = this.props.Address;

      let country: CountryModel | string | CountryArrayModel | undefined = address?.country;

      if (country) {
        if (isSafeInternational(address)) {
          country = { value: 'INTERNATIONAL' } as CountryModel;
        }

        queue.Address = { country } as LocationModel;
      } else {
        queue.Address = {} as LocationModel;
      }
    }

    this.update(queue);
  };

  updateInUsNotApplicable = (
    inUsNotApplicable: ForeignActivitiesCoOwnerItemModel['InUsNotApplicable']
  ) => {
    const queue: Partial<ForeignActivitiesCoOwnerItemModel> = { InUsNotApplicable: inUsNotApplicable };
    if (inUsNotApplicable?.applicable === false) {
      queue.Address = {} as LocationModel;
    }
    this.update(queue);
  };

  render() {
    const prefix = this.props.prefix;
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;
    const isOther = (a: string) => a === 'Other';

    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div className="co-owner">

        <Field
          title={i18n.t('foreign.pvq.activities.direct.interest.primary.who')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CoOwnerType`}
        >
          <Dropdown
            attribute={`${attribute}.CoOwnerType`}
            name="CoOwner"
            onError={this.props.onError}
            onUpdate={this.updateCoOwnerType}
            value={this.props.CoOwnerType?.value}
            required={this.props.required}
          >
            {foreignInterestCoOwnerOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>

          {isOther(this.props.CoOwnerType?.value) && (
          <Field
            title="Please explain."
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CoOwnerTypeOtherExplanation`}
          >
            <Textarea
              name="other-type-explanation"
              className="other-type-explanation"
              label={i18n.t(`foreign.pvq.contacts.heading.explanation`)}
              {...this.props.CoOwnerTypeOtherExplanation}
              onUpdate={this.updateCoOwnerOtherTypeExplanation}
              onError={this.handleError}
              required={this.props.required}
              attribute={`${attribute}.CoOwnerTypeOtherExplanation`}
            />
          </Field>

          )}
        </Field>

        <Field
          title={i18n.t(`foreign.pvq.activities.direct.heading.relationshipNature`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Relationship`}
        >
          <NotApplicable
            name="RelationshipNotApplicable"
            {...this.props.RelationshipNotApplicable}
            label={i18n.t(`foreign.pvq.activities.direct.interest.coOwner.label.none`)}
            onUpdate={this.updateRelationshipNotApplicable}
            attribute={`${attribute}.RelationshipNotApplicable`}
            includeNAConfirmation={Boolean(this.props.RelationshipNature?.value)}
          >
            <Dropdown
              attribute={`${attribute}.Relationship`}
              name="Relationship"
              onError={this.props.onError}
              onUpdate={this.updateRelationship}
              value={this.props.Relationship?.value}
              required={this.props.required}
            >
              {foreignContactRelationshipOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Dropdown>
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t(`foreign.pvq.activities.direct.heading.name`)}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
        >
          <PVQName
            name="Name"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
            showLastFirstMiddleNameOrder
          />
        </Field>

        <Field
          title={i18n.t(`foreign.pvq.activities.direct.heading.countries`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Countries`}
        >
          <NotApplicable
            name="country-not-applicable"
            {...this.props.CountryNotApplicable}
            label={i18n.t('reference.label.idk')}
            onUpdate={this.updateCountryNotApplicable}
            attribute={`${attribute}.CountryNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Countries?.value)}
          >
            <Country
              name="Countries"
              {...this.props.Countries}
              multiple
              onUpdate={this.updateCountries}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Countries`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t(`foreign.pvq.activities.direct.heading.isUs`)}
          optional
          adjustFor="address"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}

        >
          <PVQLocationUnknown
            {...this.props.Address}
            attribute={`${attribute}.Address`}
            addressUnknown={this.props.AddressNotApplicable}
            inUsaUnknown={this.props.InUsNotApplicable}
            name="foreign-national-address"
            onUpdate={this.updateAddress} // eslint-disable-line @typescript-eslint/unbound-method
            onUpdateAddressUnknown={this.updateAddressNotApplicable}
            onUpdateInUsaUnknown={this.updateInUsNotApplicable}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            required={this.props.required}
            geocode
            showAddressUnknown
            showInUsaUnknown
            showCityUnknown
            showInternationalStreet
            showIsInstallationUnknownIfDomestic
            showIsInstallationUnknownIfInternational
            showMilitaryInstallation
            hideInstallationNameUnknownWhenForeign
            showApoFpoUnknown
            showStreetUnknown
            showInternationalStreetUnknown
            showZipcodeUnknown
            label=""
            domesticTitle={i18n.t(`foreign.pvq.contacts.heading.addressDomesticTitle`)}
            foreignTitle={i18n.t(`foreign.pvq.contacts.heading.addressForeignTitle`)}
          />
        </Field>
      </div>
    ) : (
      <div className="co-owner">
        <Field
          title={i18n.t(`foreign.${prefix}.heading.name`)}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Name"
            {...this.props.Name}
            onUpdate={this.updateName}
            aria-label="Provide the full name of this foreign national"
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
          />
        </Field>

        <Field
          title={i18n.t(`foreign.${prefix}.heading.address`)}
          optional
          adjustFor="address"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <Location
            name="Address"
            {...this.props.Address}
            label={i18n.t(`foreign.${prefix}.label.address`)}
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            showPostOffice
            dispatch={this.props.dispatch}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Address`}
          />
        </Field>

        <Field
          title={i18n.t(`foreign.${prefix}.heading.countries`)}
          help={`foreign.${prefix}.help.countries`}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Countries`}
        >
          <Country
            name="Countries"
            {...this.props.Countries}
            multiple
            onUpdate={this.updateCountries}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Countries`}
          />
        </Field>

        <Field
          title={i18n.t(`foreign.${prefix}.heading.relationshipNature`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.RelationshipNature`}
        >
          <Textarea
            name="RelationshipNature"
            className="relationship-nature"
            {...this.props.RelationshipNature}
            onUpdate={this.updateRelationshipNature}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.RelationshipNature`}
          />
        </Field>
      </div>
    );
  }
}

CoOwner.defaultProps = {
  prefix: 'coOwner',
  addressBooks: {},
  dispatch: () => {},
  onUpdate: (queue) => {},
  onError: (value, arr: ErrorCode) => arr,
  // PVQ
  CoOwnerTypeOtherExplanation: {},
  CoOwnerType: {},
  RelationshipNotApplicable: { applicable: true },
  CountryNotApplicable: { applicable: true },
  AddressNotApplicable: { applicable: true },

};
